import { Inject, Injectable, InjectionToken } from '@angular/core';
import { SgOidcLogin } from '@sg/auth/oidc';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { SgMixpanelService } from './mixpanel/mixpanel.service';
import { SgUserProfile } from './sg-user-profile';

export const UserSessionAuthAdapterToken = new InjectionToken<any>('UserSessionAuthAdapterToken');

export interface UserSessionRenewEvent {
  event: 'renew';
}

export interface UserSessionRenewRetryEvent {
  event: 'renewRetry';
  retriesLeft: number;
  error: Error;
}

export interface UserSessionRenewErrorEvent {
  event: 'renewError';
  error: Error;
}

export interface UserSessionLogoutEvent {
  event: 'logout';
}

export interface UserSessionLogoutErrorEvent {
  event: 'logoutError';
  error: Error;
}

export type UserSessionEvent =
  | UserSessionRenewEvent
  | UserSessionRenewRetryEvent
  | UserSessionRenewErrorEvent
  | UserSessionLogoutEvent
  | UserSessionLogoutErrorEvent;

@Injectable()
export class UserSessionService<T extends SgOidcLogin = SgOidcLogin> {
  userProfile$ = new BehaviorSubject<SgUserProfile>(null);
  get userProfile(): SgUserProfile {
    return this.userProfile$.value;
  }
  readonly authAdapter: T;
  readonly events$: Observable<UserSessionEvent>;

  private renew = {
    interval: 0,
    whenExpiresIn: 0,
    timeoutHandler: 0,
    retries: 0,
  };

  private loggedOut: boolean;

  /** True once the user logged out in this page (or no user was logged in at load). */
  get isLoggedOut(): boolean {
    return this.loggedOut;
  }
  private events = new Subject<UserSessionEvent>();
  private renewalInFlight: Promise<UserSessionRenewEvent | UserSessionRenewErrorEvent> | null = null;

  constructor(
    public env: EnvironmentService,
    @Inject(UserSessionAuthAdapterToken) authAdapterFactory: () => SgOidcLogin,
    private mixpanelService: SgMixpanelService
  ) {
    this.authAdapter = <any>authAdapterFactory();
    this.events$ = this.events.asObservable();

    this.loggedOut = !this.authAdapter.user;

    if (!this.loggedOut) {
      this.userProfile$.next(new SgUserProfile(this.authAdapter.userProfile));
    }

    this.renew.interval = (env.oidcRenewPolicy.interval || 10) * 1000; // convert sec to ms
    this.renew.whenExpiresIn = env.oidcRenewPolicy.renewWhenExpiresIn || 50; // convert sec to ms
    this.renew.retries = env.oidcRenewPolicy.retries || 2;

    this.checkAndRenew(this.renew.retries);
  }

  logout(): Promise<any> {
    this.loggedOut = true;
    clearTimeout(this.renew.timeoutHandler);

    // End the Mixpanel identity + replay before the signout redirect navigates away (the 'logout'
    // event below fires too late — the page is already leaving). No-op if Mixpanel wasn't loaded,
    // so this is safe for apps/users that never consented. Other vendors and the server-side
    // consent record are untouched.
    this.mixpanelService.reset();

    return this.authAdapter
      .signoutRedirect()
      .then(() => this.events.next({ event: 'logout' }))
      .catch((error) => this.events.next({ event: 'logoutError', error }));
  }

  renewToken(): Promise<UserSessionRenewEvent | UserSessionRenewErrorEvent> {
    if (this.renewalInFlight) {
      return this.renewalInFlight;
    }
    this.renewalInFlight = (<any>this.authAdapter.renewToken())
      .then((result) => {
        this.renewalInFlight = null;
        switch (result.result) {
          case 'success':
            const event: UserSessionRenewEvent = { event: 'renew' };
            this.events.next(event);
            return event;
          case 'notLoggedIn':
          case 'error':
            const errorEvent: UserSessionRenewErrorEvent = {
              event: 'renewError',
              error: result.error,
            };
            this.events.next(errorEvent);
            return errorEvent;
        }
      })
      .catch((err) => {
        this.renewalInFlight = null;
        throw err;
      });
    return this.renewalInFlight;
  }

  private checkAndRenew(retriesLeft: number): void {
    if (this.authAdapter.user && !this.loggedOut) {
      if (this.authAdapter.expiresIn() <= this.renew.whenExpiresIn) {
        this.renewToken().then((event) => {
          if (event.event === 'renewError') {
            if (retriesLeft > 0) {
              --retriesLeft;
              this.events.next({ event: 'renewRetry', error: event.error, retriesLeft });
              return this.checkAndRenew(retriesLeft);
            }
          }
          this.setNextCheck();
          return event;
        });
      } else {
        this.setNextCheck();
      }
    }
  }

  private setNextCheck(): void {
    this.renew.timeoutHandler = <any>setTimeout(() => this.checkAndRenew(this.renew.retries), this.renew.interval);
  }
}
